import React, { useState, useEffect, useRef } from 'react';
import { Modal, Select, Button, Tag, Alert, Spin } from 'antd';
import { message } from '../../utils/antdMessage';

const { Option } = Select;

const ORG_ALL = '\u6240\u6709'; // 所有

/**
 * 用户队员可见范围配置弹窗
 * 打开时从服务端回显已保存配置；添加/删除后立即持久化，避免「只添加不保存」导致刷新丢失。
 */
export default function UserMemberScopeModal({
  visible,
  userId,
  userName,
  onClose,
  apiRequest
}) {
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const lastLoadedRef = useRef('[]');

  const normalizedUserId = userId != null && userId !== '' ? Number(userId) : null;

  useEffect(() => {
    if (visible && normalizedUserId) {
      fetchData();
    }
    if (!visible) {
      setSelectedOrgs([]);
      setSelectedGroups([]);
      setLoadError('');
    }
  }, [visible, normalizedUserId]);

  const applyLoadedScopes = (list) => {
    const next = Array.isArray(list) ? list : [];
    setScopes(next);
    lastLoadedRef.current = JSON.stringify(next);
    // 回填下拉：组织级 -> selectedOrgs；小组级按 org 汇总到 selectedGroups（仅展示用，不强制）
    const orgs = next.filter((s) => s.scope_type === 'org').map((s) => s.org_name);
    setSelectedOrgs(orgs.length ? [...new Set(orgs)] : []);
    const groups = next
      .filter((s) => s.scope_type === 'group' && s.group_name)
      .map((s) => s.group_name);
    setSelectedGroups(groups.length ? [...new Set(groups)] : []);
  };

  const fetchData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [scopeData, orgData] = await Promise.all([
        apiRequest(`/api/admin/user-member-scope/${normalizedUserId}`),
        apiRequest('/api/admin/org-group-options')
      ]);

      if (scopeData && scopeData.success) {
        applyLoadedScopes(scopeData.scopes || []);
        if (scopeData.tableNotExists) {
          message.warning('user_member_scope表不存在,请先执行数据库迁移脚本');
        }
      } else {
        applyLoadedScopes([]);
        const err = (scopeData && (scopeData.error || scopeData.detail)) || '加载可见范围失败';
        setLoadError(err);
        message.error(err);
      }

      if (orgData && orgData.success) {
        setOrganizations(orgData.organizations || []);
      } else if (orgData && orgData.error) {
        message.warning(orgData.error || '组织选项加载失败');
      }
    } catch (error) {
      applyLoadedScopes([]);
      setLoadError('加载数据失败');
      message.error('加载数据失败');
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const persistScopes = async (nextScopes, { closeAfter = false, successMsg } = {}) => {
    setSaving(true);
    try {
      const response = await apiRequest(`/api/admin/user-member-scope/${normalizedUserId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scopes: nextScopes })
      });

      if (response && response.success) {
        setScopes(nextScopes);
        lastLoadedRef.current = JSON.stringify(nextScopes);
        message.success(successMsg || '队员可见范围已保存');
        // 保存后再拉一次，确认库里真有数据
        try {
          const confirm = await apiRequest(`/api/admin/user-member-scope/${normalizedUserId}`);
          if (confirm && confirm.success) {
            applyLoadedScopes(confirm.scopes || []);
          }
        } catch (_) {}
        if (closeAfter) onClose();
        return true;
      }
      const err = (response && (response.error || response.detail)) || '保存失败';
      message.error(err);
      return false;
    } catch (error) {
      message.error('保存失败:网络错误');
      console.error('保存失败:', error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleAddOrgScope = async () => {
    if (!selectedOrgs || selectedOrgs.length === 0) {
      message.warning('请选择组织');
      return;
    }

    const newScopes = [...scopes];
    let addedCount = 0;

    selectedOrgs.forEach((org) => {
      const exists = newScopes.some((s) => s.scope_type === 'org' && s.org_name === org);
      if (!exists) {
        // 组织级覆盖该组织下已有小组级
        for (let i = newScopes.length - 1; i >= 0; i--) {
          if (newScopes[i].scope_type === 'group' && newScopes[i].org_name === org) {
            newScopes.splice(i, 1);
          }
        }
        newScopes.push({ scope_type: 'org', org_name: org, group_name: null });
        addedCount++;
      }
    });

    if (addedCount === 0) {
      message.warning('所选组织已全部存在');
      return;
    }

    const ok = await persistScopes(newScopes, {
      successMsg: `已添加并保存 ${addedCount} 条组织级可见范围`
    });
    if (ok) {
      setSelectedOrgs([]);
    }
  };

  const handleAddGroupScope = async () => {
    if (!selectedOrgs || selectedOrgs.length === 0) {
      message.warning('请先选择组织');
      return;
    }
    if (!selectedGroups || selectedGroups.length === 0) {
      message.warning('请选择小组');
      return;
    }

    let newScopes = [...scopes];
    let addedCount = 0;

    selectedOrgs.forEach((org) => {
      selectedGroups.forEach((group) => {
        if (group === ORG_ALL) {
          const exists = newScopes.some((s) => s.scope_type === 'org' && s.org_name === org);
          if (!exists) {
            newScopes = newScopes.filter(
              (s) => !(s.scope_type === 'group' && s.org_name === org)
            );
            newScopes.push({ scope_type: 'org', org_name: org, group_name: null });
            addedCount++;
          }
        } else {
          const hasOrgScope = newScopes.some((s) => s.scope_type === 'org' && s.org_name === org);
          if (hasOrgScope) return;

          const exists = newScopes.some(
            (s) =>
              s.scope_type === 'group' && s.org_name === org && s.group_name === group
          );
          if (!exists) {
            newScopes.push({
              scope_type: 'group',
              org_name: org,
              group_name: group
            });
            addedCount++;
          }
        }
      });
    });

    if (addedCount === 0) {
      message.warning('所选范围已全部添加或已有更高级别权限');
      return;
    }

    const ok = await persistScopes(newScopes, {
      successMsg: `已添加并保存 ${addedCount} 条可见范围`
    });
    if (ok) {
      setSelectedOrgs([]);
      setSelectedGroups([]);
    }
  };

  const handleRemoveScope = async (index) => {
    const newScopes = [...scopes];
    newScopes.splice(index, 1);
    await persistScopes(newScopes, { successMsg: '已删除并保存' });
  };

  const handleSave = async () => {
    await persistScopes(scopes, { closeAfter: true, successMsg: '队员可见范围配置成功' });
  };

  const getGroupsForSelectedOrgs = () => {
    if (!selectedOrgs || selectedOrgs.length === 0) return [];

    const allGroups = new Set();
    selectedOrgs.forEach((orgName) => {
      const org = organizations.find((o) => o.org_name === orgName);
      if (org && org.groups) {
        org.groups.forEach((g) => allGroups.add(g));
      }
    });

    const groupArray = Array.from(allGroups);
    const sorted = groupArray.filter((g) => g !== ORG_ALL).sort();
    return allGroups.has(ORG_ALL) ? [ORG_ALL, ...sorted] : sorted;
  };

  const isDirty = JSON.stringify(scopes) !== lastLoadedRef.current;

  return (
    <Modal
      title={`配置队员可见范围 - ${userName || '用户'}`}
      open={visible}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {isDirty ? '关闭(有未保存修改请点保存)' : '关闭'}
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          保存并关闭
        </Button>
      ]}
    >
      <Spin spinning={loading || saving}>
        {loadError ? (
          <Alert
            message="加载失败"
            description={loadError}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={fetchData}>
                重试
              </Button>
            }
          />
        ) : null}

        <Alert
          message="说明"
          description={
            <div>
              <p>· 系统管理员(role=admin)不受此限,自动拥有全部可见权限</p>
              <p>· <strong>组织级</strong>:可见该组织下所有小组的队员</p>
              <p>· <strong>小组级</strong>:只可见该组织下的特定小组队员</p>
              <p>· <strong>组织选「所有」</strong>:可见全部组织的队员信息</p>
              <p>· 添加/删除会<strong>立即写入数据库</strong>，刷新后仍会回显</p>
              <p>· 若用户没有任何配置且不是管理员,则看不到任何队员</p>
            </div>
          }
          type="info"
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
          <h4 style={{ marginTop: 0 }}>添加组织级可见范围(可见整个组织)</h4>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
            提示:选择组织项「所有」可查看全部组织的队员信息
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Select
              mode="multiple"
              placeholder="选择组织(可多选)"
              style={{ flex: 1 }}
              value={selectedOrgs}
              onChange={setSelectedOrgs}
              showSearch
              filterOption={(input, option) =>
                String(option?.children || '').toLowerCase().includes(input.toLowerCase())
              }
              maxTagCount="responsive"
            >
              <Option key={ORG_ALL} value={ORG_ALL}>
                所有(全部组织)
              </Option>
              {organizations.map((org) => (
                <Option key={org.org_name} value={org.org_name}>
                  {org.org_name}
                </Option>
              ))}
            </Select>
            <Button type="primary" onClick={handleAddOrgScope} loading={saving}>
              添加并保存
            </Button>
          </div>
        </div>

        <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
          <h4 style={{ marginTop: 0 }}>添加小组级可见范围(只可见特定小组)</h4>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
            提示:选择小组「所有」等同于授予该组织的可见权限
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Select
              mode="multiple"
              placeholder="选择组织(可多选)"
              style={{ flex: 1 }}
              value={selectedOrgs}
              onChange={(value) => {
                setSelectedOrgs(value);
                setSelectedGroups([]);
              }}
              showSearch
              filterOption={(input, option) =>
                String(option?.children || '').toLowerCase().includes(input.toLowerCase())
              }
              maxTagCount="responsive"
            >
              {organizations.map((org) => (
                <Option key={org.org_name} value={org.org_name}>
                  {org.org_name}
                </Option>
              ))}
            </Select>
            <Select
              mode="multiple"
              placeholder="选择小组(可多选)"
              style={{ flex: 1 }}
              value={selectedGroups}
              onChange={setSelectedGroups}
              disabled={!selectedOrgs || selectedOrgs.length === 0}
              showSearch
              filterOption={(input, option) =>
                String(option?.children || '').toLowerCase().includes(input.toLowerCase())
              }
              maxTagCount="responsive"
            >
              {getGroupsForSelectedOrgs().map((group) => (
                <Option key={group} value={group}>
                  {group === ORG_ALL ? '所有(该组织下全部小组)' : group}
                </Option>
              ))}
            </Select>
            <Button type="primary" onClick={handleAddGroupScope} loading={saving}>
              添加并保存
            </Button>
          </div>
        </div>

        <div>
          <h4>当前配置({scopes.length}条)</h4>
          {scopes.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>
              暂无配置,该用户将看不到任何队员
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {scopes.map((scope, index) => {
                let displayText = '';
                if (scope.scope_type === 'org') {
                  if (scope.org_name === ORG_ALL) {
                    displayText = `★ 所有(全部组织)`;
                  } else {
                    displayText = `★ ${scope.org_name}(全部小组)`;
                  }
                } else {
                  displayText = `● ${scope.org_name} / ${scope.group_name}`;
                }

                return (
                  <Tag
                    key={`${scope.scope_type}-${scope.org_name}-${scope.group_name || ''}-${index}`}
                    closable
                    onClose={(e) => {
                      e.preventDefault();
                      handleRemoveScope(index);
                    }}
                    color={
                      scope.scope_type === 'org'
                        ? scope.org_name === ORG_ALL
                          ? 'purple'
                          : 'blue'
                        : 'green'
                    }
                    style={{ marginBottom: 4, padding: '4px 8px' }}
                  >
                    {displayText}
                  </Tag>
                );
              })}
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
}